// Oficinas de confiança: as que a empresa já conhece. O SOS mecânica mostra
// estas primeiro (com a distância, quando o local está cadastrado) e depois
// as do Google Maps. O motorista também guarda uma oficina que o atendeu bem.
import { oficinas, situacoesDeEnvio } from '../dados.js';
import { h, icone, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { TIPOS_DE_PROBLEMA, localDoLink, normalizarTelefone, telefoneNaTela } from '../lib/sos.js';
import { UFS } from '../lib/validacao.js';
import { nestaTela } from '../rotas.js';
import { podeGravar } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, cartaoLink, confirmar, emblema, faixa, formulario, secao, vazio } from '../ui/componentes.js';
import { emblemaEnvio } from '../ui/sinal.js';
import { faixaDaConta } from './inicio.js';

const nomesDosTipos = (tipos) => (tipos?.length
  ? TIPOS_DE_PROBLEMA.filter((t) => tipos.includes(t.id)).map((t) => t.nome).join(', ')
  : 'Atende de tudo');

export function telaOficinas() {
  const corpo = h('div', { class: 'secao' }, carregando());
  (async () => {
    const [lista, envio] = await Promise.all([oficinas.listar(), situacoesDeEnvio('oficinas')]);
    const cartao = (o) => cartaoLink({
      href: `#/oficinas/${o.id}`,
      simbolo: 'chave',
      titulo: o.nome,
      linhas: [
        nomesDosTipos(o.tipos),
        [o.telefone && telefoneNaTela(o.telefone), [o.cidade, o.uf].filter(Boolean).join('/')].filter(Boolean).join(' · '),
      ],
      emblemas: [!o.ativo && emblema('Desativada'), o.latitude == null && emblema('Sem o local', 'alerta'), emblemaEnvio(envio.get(o.id))],
    });
    const ativas = lista.filter((o) => o.ativo);
    const inativas = lista.filter((o) => !o.ativo);
    trocar(corpo,
      podeGravar() && h('a', { class: 'botao', href: '#/oficinas/nova' }, '+ Oficina'),
      lista.length === 0
        ? vazio({ simbolo: 'chave', titulo: 'Nenhuma oficina ainda', texto: 'Cadastre as borracharias, mecânicas e guinchos de confiança. No SOS mecânica, elas aparecem primeiro para o motorista.' })
        : secao('Oficinas de confiança', h('div', { class: 'lista' },
          ativas.map(cartao),
          inativas.length > 0 && h('h3', { class: 'pequeno' }, 'Desativadas'),
          inativas.map(cartao)), { contador: String(ativas.length) }),
      h('a', { class: 'botao secundario cheio', href: '#/sos' }, icone('alerta', { tamanho: 20 }), 'Abrir o SOS mecânica'));
  })().catch((erro) => trocar(corpo, faixa(mensagemDeErro(erro), 'perigo')));
  return casca({ titulo: 'Oficinas de confiança', voltar: '/mais', conteudo: [faixaDaConta(), corpo] });
}

export function telaOficina({ params }) {
  const tela = nestaTela();
  const nova = !params.id;
  const corpo = h('div', { class: 'secao' }, carregando());
  const titulo = h('span', null, nova ? 'Nova oficina' : 'Oficina');

  (async () => {
    const o = nova ? null : await oficinas.obter(params.id);
    if (!nova && !o) {
      trocar(corpo, vazio({ simbolo: 'chave', titulo: 'Oficina não encontrada', acao: h('a', { class: 'botao', href: '#/oficinas' }, 'Ver as oficinas') }));
      return;
    }
    if (o) titulo.textContent = o.nome;
    const gravavel = podeGravar();
    const localTexto = o?.latitude != null ? `${Number(o.latitude)}, ${Number(o.longitude)}` : '';
    const situacaoLocal = h('p', { class: 'dica', 'aria-live': 'polite' });
    const campoLocal = campo({
      rotulo: 'Local no mapa', nome: 'local', valor: localTexto,
      dica: 'Cole o link do Google Maps da oficina, ou toque em "Estou na oficina agora".',
      atributos: { autocomplete: 'off', inputmode: 'url' },
      aoMudar: () => conferirLocal(),
    });
    const botaoAqui = h('button', {
      class: 'botao secundario',
      type: 'button',
      disabled: !gravavel,
      onClick: () => {
        if (!navigator.geolocation) {
          avisar('Este aparelho não informa a localização.', 'erro');
          return;
        }
        botaoAqui.disabled = true;
        navigator.geolocation.getCurrentPosition((p) => {
          campoLocal.querySelector('input').value = `${p.coords.latitude.toFixed(6)}, ${p.coords.longitude.toFixed(6)}`;
          botaoAqui.disabled = false;
          conferirLocal();
        }, () => {
          botaoAqui.disabled = false;
          avisar('Não deu para pegar a localização. Confira se o GPS está ligado e se o app tem permissão.', 'erro');
        }, { enableHighAccuracy: true, timeout: 20000 });
      },
    }, icone('local', { tamanho: 20 }), 'Estou na oficina agora');

    function conferirLocal() {
      const texto = campoLocal.querySelector('input').value.trim();
      const ponto = localDoLink(texto);
      situacaoLocal.textContent = !texto ? 'Sem o local, a oficina aparece no SOS sem a distância.'
        : ponto ? `Local: ${ponto.latitude.toFixed(5)}, ${ponto.longitude.toFixed(5)}`
          : 'Não achei o local neste link. Use o link completo do Google Maps (abra a oficina no Maps → Compartilhar → Copiar link e abra no navegador antes de copiar).';
    }

    const form = formulario({
      filhos: [
        campo({ rotulo: 'Nome', nome: 'nome', obrigatorio: true, valor: o?.nome ?? '', atributos: { autocomplete: 'off' } }),
        campo({
          rotulo: 'Telefone com DDD', nome: 'telefone', tipo: 'tel', valor: o?.telefone ? telefoneNaTela(o.telefone) : '',
          atributos: { inputmode: 'tel', autocomplete: 'off', placeholder: '(48) 99999-0000' },
        }),
        campo({ rotulo: 'Este número tem WhatsApp', nome: 'whatsapp', tipo: 'caixa', valor: o?.whatsapp ?? false }),
        campo({ rotulo: 'Endereço', nome: 'endereco', valor: o?.endereco ?? '', dica: 'Rua ou rodovia e km.' }),
        h('div', { class: 'grade-uf' },
          campo({ rotulo: 'Cidade', nome: 'cidade', valor: o?.cidade ?? '', formato: 'nome' }),
          campo({ rotulo: 'UF', nome: 'uf', tipo: 'select', valor: o?.uf ?? '', opcoes: [['', '—'], ...UFS.map((u) => [u, u])] })),
        h('fieldset', { class: 'campo' },
          h('legend', null, 'O que atende'),
          h('div', { class: 'tipos-oficina' },
            TIPOS_DE_PROBLEMA.map((t) => campo({ rotulo: t.nome, nome: `tipo_${t.id}`, tipo: 'caixa', valor: o?.tipos?.includes(t.id) ?? false }))),
          h('p', { class: 'dica' }, 'Sem nada marcado, aparece para qualquer problema.')),
        campoLocal,
        botaoAqui,
        situacaoLocal,
        campo({ rotulo: 'Observação', nome: 'observacao', tipo: 'textarea', valor: o?.observacao ?? '', dica: 'Ex.: atende 24 h, aceita cartão, falar com o Zé.' }),
        o && campo({ rotulo: 'Oficina ativa (aparece no SOS)', nome: 'ativo', tipo: 'caixa', valor: o.ativo }),
      ],
      textoBotao: nova ? 'Cadastrar' : 'Salvar alterações',
      somenteLeitura: !gravavel,
      validar: (d) => {
        const erros = {};
        if (d.nome.length < 2) erros.nome = 'Informe o nome.';
        if (d.telefone && !normalizarTelefone(d.telefone)) erros.telefone = 'Informe DDD + número, como (48) 99999-0000.';
        if (d.local && !localDoLink(d.local)) erros.local = 'Não achei o local neste link.';
        return erros;
      },
      aoEnviar: async (d) => {
        const ponto = d.local ? localDoLink(d.local) : null;
        const telefone = d.telefone ? normalizarTelefone(d.telefone) : null;
        const dados = {
          nome: d.nome,
          telefone,
          whatsapp: Boolean(telefone) && d.whatsapp === true,
          endereco: d.endereco || null,
          cidade: d.cidade || null,
          uf: d.uf || null,
          tipos: TIPOS_DE_PROBLEMA.filter((t) => d[`tipo_${t.id}`] === true).map((t) => t.id),
          latitude: ponto ? Number(ponto.latitude.toFixed(6)) : null,
          longitude: ponto ? Number(ponto.longitude.toFixed(6)) : null,
          observacao: d.observacao || null,
          ...(o ? { ativo: d.ativo === true } : {}),
        };
        if (nova) {
          await oficinas.criar(dados);
          avisar('Oficina cadastrada.');
        } else {
          await oficinas.atualizar(o.id, dados, o);
          avisar('Alterações salvas.');
        }
        tela.ir('/oficinas', { substituir: true });
      },
      extras: o && gravavel && h('button', {
        class: 'botao texto',
        type: 'button',
        onClick: async () => {
          const ok = await confirmar({ titulo: 'Excluir esta oficina?', texto: 'Ela sai da lista e do SOS.', sim: 'Excluir', perigo: true });
          if (!ok) return;
          try {
            await oficinas.excluir(o.id);
            avisar('Oficina excluída.');
            tela.ir('/oficinas', { substituir: true });
          } catch (erro) {
            avisar(mensagemDeErro(erro), 'erro');
          }
        },
      }, 'Excluir oficina'),
    });
    conferirLocal();
    trocar(corpo, form);
  })().catch((erro) => trocar(corpo, faixa(mensagemDeErro(erro), 'perigo')));

  return casca({ titulo, voltar: '/oficinas', conteudo: corpo });
}
